import { useState, useEffect, useCallback } from 'react'
import { formatDateForAPI } from '@meiuca/web-foundation'
import { venueBookingService } from '@/services/venueBookingService'
import type { AvailableSchedule, CourtAvailability } from '@/services/venueBookingService'

interface UseCourtAvailabilityParams {
  venueId: string | null
  currentWeekStart: Date
  modality?: string | null
  enabled?: boolean
}

interface CourtAvailabilityData {
  availableSchedules: AvailableSchedule[]
  courts: CourtAvailability[]
}

interface UseCourtAvailabilityReturn {
  availability: CourtAvailabilityData | null
  loading: boolean
  error: string | null
  refetch: () => void
}

const availabilityCache = new Map<string, CourtAvailabilityData>()

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date)
  nextDate.setDate(nextDate.getDate() + days)
  return nextDate
}

const startOfDay = (date: Date) => {
  const day = new Date(date)
  day.setHours(0, 0, 0, 0)
  return day
}

const nextFullHour = (date: Date) => {
  const hour = new Date(date)
  hour.setMinutes(0, 0, 0)
  hour.setHours(hour.getHours() + 1)
  return hour
}

const cacheKeyForDate = (
  venueId: string,
  date: Date,
  modality?: string | null,
  now = new Date()
) => {
  const dateKey = date.toISOString().slice(0, 10)
  const isToday = startOfDay(date).getTime() === startOfDay(now).getTime()
  const freshnessKey = isToday ? nextFullHour(now).toISOString() : 'day'
  return `${venueId}-${dateKey}-${freshnessKey}-${modality || 'all'}`
}

const mergeAvailability = (items: CourtAvailabilityData[]): CourtAvailabilityData => {
  const courts = new Map<string, CourtAvailability>()
  const availableSchedules: AvailableSchedule[] = []

  items.forEach(item => {
    item.courts.forEach(court => courts.set(court.id, court))
    availableSchedules.push(...item.availableSchedules)
  })

  return {
    courts: Array.from(courts.values()),
    availableSchedules: availableSchedules.sort((a, b) => a.date.localeCompare(b.date))
  }
}

export function useCourtAvailability({
  venueId,
  currentWeekStart,
  modality,
  enabled = true
}: UseCourtAvailabilityParams): UseCourtAvailabilityReturn {
  const [availability, setAvailability] = useState<CourtAvailabilityData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAvailability = useCallback(async () => {
    if (!venueId || !enabled) {
      setAvailability(null)
      setLoading(false)
      setError(null)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const now = new Date()
      const today = startOfDay(now)
      const weekDates = Array.from({ length: 7 }, (_, index) => startOfDay(addDays(currentWeekStart, index)))
      const requestDates = weekDates.filter(date => date >= today)

      if (requestDates.length === 0) {
        setAvailability({ availableSchedules: [], courts: [] })
        setLoading(false)
        return
      }

      const daysAvailability = await Promise.all(
        requestDates.map(async date => {
          const cacheKey = cacheKeyForDate(venueId, date, modality, now)
          const cached = availabilityCache.get(cacheKey)
          if (cached) return cached

          const data = await venueBookingService.getCourtAvailability(venueId, {
            date: formatDateForAPI(date),
            modality: modality || undefined
          })
          availabilityCache.set(cacheKey, data)
          return data
        })
      )

      const data = mergeAvailability(daysAvailability)
      setAvailability(data)
    } catch (err) {
      console.error('[useCourtAvailability] Failed to fetch court availability', err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar disponibilidade')
      setAvailability(null)
    } finally {
      setLoading(false)
    }
  }, [venueId, currentWeekStart, modality, enabled])

  useEffect(() => {
    fetchAvailability()
  }, [fetchAvailability])

  return {
    availability,
    loading,
    error,
    refetch: fetchAvailability
  }
}
